import { useState } from "react";
import { StyleSheet, Text, View, type LayoutChangeEvent } from "react-native";
import { useTranslation } from "react-i18next";
import type { Draft } from "@heraion/shared";

import { Button } from "../primitives/Button";
import { IconButton } from "../primitives/IconButton";
import type { UiTheme } from "../theme";
import { CopyToDaysTray } from "./CopyToDaysTray";
import type { V3SelectionActions } from "./useV3SelectionActions";

// The bar under the day while «Select» is on (#336, brief §7.2, mock :531):
// how many are selected with ↑ ↓ (one contiguous run only), then «Copy to…»,
// which opens the days tray above the bar, «Rule» (the builder for the
// selection, slice 5), and «Remove». Nothing selected: all off.
export function SelectionBar({
  theme,
  draft,
  sIdx,
  count,
  actions,
  onRule,
  onLayout,
}: {
  theme: UiTheme;
  draft: Draft;
  sIdx: number;
  count: number;
  actions: V3SelectionActions;
  /** Opens the «Rule» sheet on the selected exercises. */
  onRule: () => void;
  /** Its height, so the shell's toast can sit above it. */
  onLayout?: (e: LayoutChangeEvent) => void;
}) {
  const { t } = useTranslation();
  const [copying, setCopying] = useState(false);
  const styles = makeStyles(theme);
  const none = count === 0;
  return (
    <View style={styles.bar} onLayout={onLayout}>
      {copying && !none ? (
        <CopyToDaysTray
          theme={theme}
          draft={draft}
          sIdx={sIdx}
          what={t("editorV3.nExercises", { count, defaultValue: "{{count}} exercises" })}
          onCopy={(days) => {
            actions.copyToDays(days);
            setCopying(false);
          }}
          onCancel={() => setCopying(false)}
        />
      ) : null}
      {/* Two rows: the count and the arrows, then the two worded actions, so
          «Kopieren in…» and «Удалить» keep their words whole at 360 pt. */}
      <View style={styles.row}>
        <Text
          style={styles.count}
          // Only the count speaks when it changes, not the whole bar.
          accessibilityLiveRegion="polite"
          aria-live="polite"
        >
          {t("editorV3.selectedCount", { count, defaultValue: "{{count}} selected" })}
        </Text>
        <IconButton
          theme={theme}
          name="chevron-up"
          accessibilityLabel={t("program.moveUp", { defaultValue: "Move up" })}
          onPress={() => actions.move(-1)}
          disabled={!actions.canMove(-1)}
        />
        <IconButton
          theme={theme}
          name="chevron-down"
          accessibilityLabel={t("program.moveDown", { defaultValue: "Move down" })}
          onPress={() => actions.move(1)}
          disabled={!actions.canMove(1)}
        />
      </View>
      <View style={styles.row}>
        <Button
          theme={theme}
          variant="secondary"
          style={styles.action}
          numberOfLines={2}
          // Vertical only: sideways slop would reach the next button.
          hitSlop={{ top: 8, bottom: 8 }}
          label={t("editorV3.copyTo", { defaultValue: "Copy to…" })}
          onPress={() => setCopying(true)}
          disabled={none || draft.sessions.length < 2}
        />
        <Button
          theme={theme}
          variant="secondary"
          style={styles.action}
          numberOfLines={2}
          hitSlop={{ top: 8, bottom: 8 }}
          label={t("editorV3.rule.selectionAction", { defaultValue: "Rule" })}
          onPress={onRule}
          disabled={none}
        />
        <Button
          theme={theme}
          variant="danger"
          style={styles.action}
          numberOfLines={2}
          // Vertical only: sideways slop would reach the next button.
          hitSlop={{ top: 8, bottom: 8 }}
          label={t("editorV3.remove", { defaultValue: "Remove" })}
          onPress={actions.remove}
          disabled={none}
        />
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    bar: {
      gap: s.s3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s3,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.border,
      backgroundColor: p.surface,
    },
    row: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    action: { flex: 1 },
    count: { flex: 1, color: p.ink, fontSize: ty.sizes.sm, fontWeight: "700" },
  });
}
